// src/components/system/DemoSeeder.tsx
// Primer login de un usuario FREE sin la demo → le clonamos el proyecto de ejemplo
// "El Desvío (demo)" (edge function seed-demo) para que tenga algo completo que explorar.
// SOLO cuentas Free: en las de pago el demo no aporta y ocupa un hueco de proyecto
// activo (caso Ana). Idempotente en el backend (no duplica y también gatea por Free) +
// guarda de sesión aquí (evita dobles en remounts). Se monta una vez en AppLayout.
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAnalysisJobs } from '@/hooks/useScriptAnalysis';
import { useOrganization } from '@/hooks/useOrganization';
import { resolvePlan } from '@/lib/plans';

const SEED_FLAG = 'pelip-demo-seed-attempted';
const DEMO_NAME = 'El Desvío (demo)';

export default function DemoSeeder() {
  const { isAuthenticated } = useAuth();
  const { data: jobs, isSuccess } = useAnalysisJobs();
  const { data: org } = useOrganization();
  const qc = useQueryClient();
  const running = useRef(false);

  useEffect(() => {
    // Sembramos el demo SOLO en cuentas FREE a las que les FALTA. En cuentas de pago
    // no se siembra (el demo ocuparía un hueco de proyecto). Idempotente en el backend.
    if (!isAuthenticated || !isSuccess || running.current) return;
    if (sessionStorage.getItem(SEED_FLAG)) return;
    if (!jobs || !org) return;
    // Solo Free: si el plan de la org es de pago, no sembrar.
    if (resolvePlan(org.plan).id !== 'free') return;
    if (jobs.some((j) => j.file_name === DEMO_NAME)) return;

    running.current = true;
    sessionStorage.setItem(SEED_FLAG, '1');
    const toastId = toast.loading('Preparando tu proyecto de ejemplo (El Desvío)…');
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('seed-demo', { body: {} });
        if (error || (data as { error?: string })?.error) {
          throw new Error(error?.message || (data as { error?: string })?.error || 'fallo');
        }
        await qc.invalidateQueries({ queryKey: ['analysis-jobs'] });
        toast.success('Listo: tienes un proyecto de ejemplo para explorar.', { id: toastId });
      } catch (e) {
        console.error('[demo-seed]', e);
        toast.dismiss(toastId);
        // No molestamos al usuario con un error: si falla, simplemente no hay demo.
        // Permitimos reintentar en la próxima sesión.
        sessionStorage.removeItem(SEED_FLAG);
        running.current = false;
      }
    })();
  }, [isAuthenticated, isSuccess, jobs, org, qc]);

  return null;
}
